/**
 * 15 §2.1 static arena layer: baked once per (team, arena, pixelScale) by
 * inverse-projecting every floor pixel through the 02 §7 camera. Two images:
 * the full bake and the "grid-only floor" used for the OT un-draw region.
 * Team B's bake is the same code with the team transform.
 */
import { VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import type { Arena, Team } from '../../../sim/types.js'
import { DEFAULT_CAMERA, project, toTeamLocal, type CameraParams, type Projected } from '../camera.js'
import { gapOf } from '../../../sim/arena.js'
import { ambientOcclusion, annotationLabels, concreteValue, floodlight, floorInverse, inVoid, lightsFor, onFloor, seamDistance } from './arena-math.js'
import { BAKE_FLOOR_MAX_W, BAKE_MAX_W, INK_RGB } from './constants.js'
import { hash2 } from './rng.js'
import { drawPillar, pillarHeight } from './pillars.js'

type Canvas = OffscreenCanvas | HTMLCanvasElement

function makeCanvas(w: number, h: number): Canvas {
  return typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h })
}

export interface BakeResult { full: Canvas; gridOnly: Canvas; layers?: Record<string, Canvas>; ms: number }

export class ArenaBake {
  private cache = new Map<string, BakeResult>()

  get(arena: Arena, team: Team, pixelScale: number, opts: { debugLayers?: boolean } = {}, cam: CameraParams = DEFAULT_CAMERA): BakeResult {
    const key = `${arena.id}:${team}:${pixelScale.toFixed(3)}:${opts.debugLayers ? 'L' : ''}:${cam.back}:${cam.height}:${cam.pitchDeg}:${cam.hfovDeg}:${cam.near}`
    const hit = this.cache.get(key)
    if (hit) return hit
    const res = bake(arena, team, pixelScale, cam, !!opts.debugLayers)
    if (this.cache.size > 6) this.cache.delete(this.cache.keys().next().value!)
    this.cache.set(key, res)
    return res
  }
}

const PAPER = { r: 12, g: 14, b: 17 }

function bake(arena: Arena, team: Team, pixelScale: number, cam: CameraParams, debugLayers: boolean): BakeResult {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0
  // 15 §2.1 budget: bake at most BAKE_MAX_W wide (the renderer draws it scaled to VIRTUAL_W × VIRTUAL_H).
  const capScale = Math.min(pixelScale, BAKE_MAX_W / VIRTUAL_W)
  const W = Math.round(VIRTUAL_W * capScale), H = Math.round(VIRTUAL_H * capScale)
  // Floor sampled per pixel (inverse projection) on a capped grid, then upscaled with smoothing.
  const fw = Math.max(320, Math.min(BAKE_FLOOR_MAX_W, Math.round(W / 2))), fh = Math.round((fw * VIRTUAL_H) / VIRTUAL_W)
  const floor = makeCanvas(fw, fh), grid = makeCanvas(fw, fh)
  const fctx = floor.getContext('2d') as CanvasRenderingContext2D, gctx = grid.getContext('2d') as CanvasRenderingContext2D
  const img = fctx.createImageData(fw, fh), gimg = gctx.createImageData(fw, fh)
  const inv = floorInverse(team, arena, 0, cam)
  const lights = lightsFor(arena)
  const midZ = arena.length / 2
  const data = img.data, gdata = gimg.data
  const sxScale = VIRTUAL_W / fw, syScale = VIRTUAL_H / fh
  for (let py = 0; py < fh; py++) {
    for (let px = 0; px < fw; px++) {
      const i = (py * fw + px) * 4
      const p = inv((px + 0.5) * sxScale, (py + 0.5) * syScale)
      let r = PAPER.r, g = PAPER.g, b = PAPER.b
      let gr = PAPER.r, gg = PAPER.g, gb = PAPER.b
      if (p && onFloor(arena, p.x, p.z)) {
        // Field arenas draw the gap as floor (`voidLook: 'floor'`); the etched edge lines in drawAnnotations still mark it.
        const voidHere = inVoid(arena, p.x, p.z) && arena.voidLook !== 'floor'
        // Construction grid (1 m) over darkness: the void look, also the OT un-draw look.
        const gx = Math.abs(((p.x % 1) + 1) % 1 - 0.5), gz = Math.abs(((p.z % 1) + 1) % 1 - 0.5)
        const pxM = sxScale / Math.max(1, p.zc > 0 ? (VIRTUAL_W / 2) / Math.tan((cam.hfovDeg * Math.PI) / 360) / p.zc : 1) // metres per sample px
        const gridLine = Math.min(gx, gz) > 0.5 - pxM * 0.9
        const gridV = gridLine ? 0.38 : 0.0
        gr = PAPER.r + 232 * gridV * 0.35; gg = PAPER.g + 236 * gridV * 0.35; gb = PAPER.b + 239 * gridV * 0.35
        if (voidHere) { r = gr; g = gg; b = gb }
        else {
          const n = concreteValue(p.x, p.z)
          // Ambient floor term + floodlight falloff (arena-math), eased AO at the junctions.
          const light = Math.min(1.12, 0.35 + 0.55 * floodlight(p.x, p.z, 0, lights))
          const ao = ambientOcclusion(arena, p.x, p.z)
          let v = (46 + n * 40) * light * ao
          // Fine aggregate speckle at sample-pixel scale (1–2 px on screen).
          if (hash2(px, py, 11) / 4294967296 > 0.93) v += 6
          // Pour seams: 1 px ink line with a soft shadow beside it.
          const sd = seamDistance(p.x, p.z)
          if (sd < pxM * 0.7) v = v * 0.65 + 8
          else if (sd < pxM * 1.4) v *= 0.85
          // Zone tint: own pads a hair cooler than the enemy's.
          const own = (team === 0 ? p.z < midZ : p.z > midZ)
          r = v * (own ? 0.96 : 1.0); g = v * 0.99; b = v * (own ? 1.04 : 0.98)
        }
      }
      data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = 255
      gdata[i] = gr; gdata[i + 1] = gg; gdata[i + 2] = gb; gdata[i + 3] = 255
    }
  }
  fctx.putImageData(img, 0, 0)
  gctx.putImageData(gimg, 0, 0)

  const full = makeCanvas(W, H), gridOnly = makeCanvas(W, H)
  const layers: Record<string, Canvas> = {}
  const P = (x: number, z: number, y = 0): Projected => project(x, y, z, team, arena, cam)
  for (const [name, target, src] of [['full', full, floor], ['grid', gridOnly, grid]] as const) {
    const ctx = target.getContext('2d') as CanvasRenderingContext2D
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = `rgb(${PAPER.r},${PAPER.g},${PAPER.b})`
    ctx.fillRect(0, 0, W, H)
    ctx.imageSmoothingEnabled = true
    ctx.drawImage(src, 0, 0, W, H)
    ctx.setTransform(capScale, 0, 0, capScale, 0, 0)
    drawWalls(ctx, arena, team, P, name === 'grid')
    drawPillars(ctx, arena, P, name === 'grid')
    drawAnnotations(ctx, arena, team, P)
    void layers
  }
  if (debugLayers) {
    const mk = (name: string, fn: (ctx: CanvasRenderingContext2D) => void) => { const c = makeCanvas(W, H); const ctx = c.getContext('2d') as CanvasRenderingContext2D; ctx.setTransform(capScale, 0, 0, capScale, 0, 0); fn(ctx); layers[name] = c }
    mk('floor', (ctx) => { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.drawImage(floor, 0, 0, W, H) })
    mk('walls', (ctx) => drawWalls(ctx, arena, team, P, false))
    mk('pillars', (ctx) => drawPillars(ctx, arena, P, false))
    mk('annotations', (ctx) => drawAnnotations(ctx, arena, team, P))
  }
  const ms = (typeof performance !== 'undefined' ? performance.now() : 0) - t0
  return { full, gridOnly, layers: debugLayers ? layers : undefined, ms }
}

function poly(ctx: CanvasRenderingContext2D, pts: Projected[]): void {
  ctx.beginPath(); ctx.moveTo(pts[0].sx, pts[0].sy); for (const p of pts.slice(1)) ctx.lineTo(p.sx, p.sy); ctx.closePath()
}

function drawWalls(ctx: CanvasRenderingContext2D, arena: Arena, team: Team, P: (x: number, z: number, y?: number) => Projected, gridOnly: boolean): void {
  for (const w of arena.walls) {
    const a = P(w.ax, w.az), b = P(w.bx, w.bz), at = P(w.ax, w.az, w.yMax), bt = P(w.bx, w.bz, w.yMax)
    if (!a.visible && !b.visible) continue
    const isBack = Math.abs(w.az - w.bz) < 1e-6
    // A02-10: the own back wall (team-local z = 0) sits between the camera and
    // the near floor; it is "below the bottom edge" by design and must not be
    // painted over the own zone.
    if (isBack && toTeamLocal(w.ax, w.az, team, arena.length).lz < 1) continue
    // Darker concrete planes: side walls unlit and flat; the far back wall carries the floodlight rim.
    poly(ctx, [a, b, bt, at])
    ctx.fillStyle = gridOnly ? 'rgba(14,16,19,0.9)' : isBack ? '#22292f' : '#161b20'
    ctx.fill()
    if (!gridOnly && isBack) { const grad = ctx.createLinearGradient(0, at.sy, 0, a.sy); grad.addColorStop(0, 'rgba(255,255,255,0.10)'); grad.addColorStop(1, 'rgba(255,255,255,0)'); ctx.fillStyle = grad; ctx.fill() }
    // AO at the wall/floor junction: 10 px band, smoothstep alpha (0 → 0.35).
    const grad2 = ctx.createLinearGradient(0, a.sy - 8, 0, a.sy + 2)
    for (const t of [0, 0.25, 0.5, 0.75, 1]) grad2.addColorStop(t, `rgba(0,0,0,${(0.35 * t * t * (3 - 2 * t)).toFixed(3)})`)
    ctx.fillStyle = grad2; ctx.fillRect(Math.min(a.sx, b.sx), a.sy - 8, Math.abs(b.sx - a.sx) || 2, 10)
    ctx.strokeStyle = `rgba(${INK_RGB},0.5)`; ctx.lineWidth = 1
    poly(ctx, [a, b, bt, at]); ctx.stroke()
    // 1 px cap line along the top edge.
    ctx.strokeStyle = `rgba(${INK_RGB},0.35)`
    ctx.beginPath(); ctx.moveTo(at.sx, at.sy); ctx.lineTo(bt.sx, bt.sy); ctx.stroke()
  }
}

function drawPillars(ctx: CanvasRenderingContext2D, arena: Arena, P: (x: number, z: number, y?: number) => Projected, gridOnly: boolean): void {
  for (const pl of arena.pillars) drawPillar(ctx, pl, P(pl.x, pl.z), P(pl.x, pl.z, pillarHeight(pl)), gridOnly)
}

/** Etched annotations (blueprint ink, 1 px, 60 %): dimension lines, pillar crosshairs, zone labels, gap hatch. */
function drawAnnotations(ctx: CanvasRenderingContext2D, arena: Arena, team: Team, P: (x: number, z: number, y?: number) => Projected): void {
  const hw = arena.width / 2
  ctx.save()
  ctx.strokeStyle = `rgba(${INK_RGB},0.6)`; ctx.fillStyle = `rgba(${INK_RGB},0.6)`; ctx.lineWidth = 1
  ctx.font = '10px ui-monospace, monospace'; ctx.textBaseline = 'middle'
  const seg = (a: Projected, b: Projected) => { if (!a.visible || !b.visible) return; ctx.beginPath(); ctx.moveTo(a.sx, a.sy); ctx.lineTo(b.sx, b.sy); ctx.stroke() }
  const tick = (p: Projected, dx: number, dy: number) => { if (!p.visible) return; ctx.beginPath(); ctx.moveTo(p.sx - dx, p.sy - dy); ctx.lineTo(p.sx + dx, p.sy + dy); ctx.stroke() }
  const label = (text: string, p: Projected, ox = 4, oy = 0) => { if (!p.visible) return; ctx.fillText(text, p.sx + ox, p.sy + oy) }
  // S10 (spec §3): every dimension reads off the arena — zone depth along the left wall for both zones (10.00 / 13.33),
  // width along the far back wall (12.00 / 16.00), the gap from the pads (GAP 10.00 / GAP 13.33) with hatch marks on both edges.
  const labels = annotationLabels(arena), gap = gapOf(arena)
  for (const zn of arena.zones) {
    const a = P(-hw + 0.3, zn.zMin), b = P(-hw + 0.3, zn.zMax)
    seg(a, b); tick(a, 4, 0); tick(b, 4, 0)
    label(labels.zoneDepth, P(-hw + 0.3, (zn.zMin + zn.zMax) / 2), 6)
  }
  { const a = P(-hw, arena.length - 0.4), b = P(hw, arena.length - 0.4); seg(a, b); tick(a, 0, 4); tick(b, 0, 4); label(labels.width, P(0, arena.length - 0.4), 4, -8) }
  if (gap.end > gap.start) {
    const a = P(hw - 0.3, gap.start), b = P(hw - 0.3, gap.end); seg(a, b); tick(a, 4, 0); tick(b, 4, 0); label(labels.gap, P(hw - 0.3, (gap.start + gap.end) / 2), -58)
    for (let x = -hw; x <= hw; x += 1) { seg(P(x, gap.start), P(x + 0.5, gap.start + 0.5)); seg(P(x, gap.end), P(x + 0.5, gap.end - 0.5)) }
  }
  // Pillar centre crosshairs + radius callout.
  for (const pl of arena.pillars) {
    seg(P(pl.x - 1.2, pl.z), P(pl.x + 1.2, pl.z)); seg(P(pl.x, pl.z - 1.2), P(pl.x, pl.z + 1.2))
    label(`R${pl.r.toFixed(2)}`, P(pl.x + 0.9, pl.z - 0.6))
  }
  // Zone labels A / B at zone centres (etched big, faint).
  ctx.font = '22px ui-monospace, monospace'; ctx.fillStyle = `rgba(${INK_RGB},0.18)`
  for (const zn of arena.zones) { const c = P((zn.xMin + zn.xMax) / 2, (zn.zMin + zn.zMax) / 2 + 2); if (c.visible) ctx.fillText(zn.team === 0 ? 'A' : 'B', c.sx - 7, c.sy) }
  // Spawn slot crosses.
  ctx.strokeStyle = `rgba(${INK_RGB},0.35)`
  for (const tm of [0, 1] as Team[]) for (const s of arena.spawns[tm]) { seg(P(s.x - 0.3, s.z), P(s.x + 0.3, s.z)); seg(P(s.x, s.z - 0.3), P(s.x, s.z + 0.3)) }
  void team
  ctx.restore()
}
